import React from 'react';
import { Theme, makeStyles, useTheme } from '@material-ui/core/styles';
import { Grid, Box, useMediaQuery } from '@material-ui/core';

import AccountSearch from '../../account-search';
import Dropdown from '../../dropdown';
import Pagination from '../../pagination';
import { PageSize } from '../../pagination/types';
import { useTranslation } from '../../../hooks/useTranslation';
import { AccountListState } from '../multi-accounts/accountListReducer';
import { AccountSort } from '../../../__generated__/pge-types';

type StyleProps = {
  showMini: boolean;
};

const useStyles = makeStyles((theme: Theme) => ({
  formArea: {
    paddingLeft: theme.spacing(0),
    paddingRight: theme.spacing(0),
    marginTop: theme.spacing(2),
    display: 'flex',
    flex: ({ showMini }: StyleProps) => (showMini ? 1 : 0),
    flexDirection: ({ showMini }: StyleProps) => (showMini ? 'column' : 'row'),
    marginBottom: ({ showMini }: StyleProps) =>
      showMini ? theme.spacing(0) : undefined,
  },
  formControl: {
    flex: 1,
    '& .MuiOutlinedInput-input': {
      padding: `${theme.spacing(2)}px ${theme.spacing(2)}px`,
    },
    '& .MuiOutlinedInput-inputAdornEnd': {
      paddingRight: 0,
    },
    width: ({ showMini }: StyleProps) => (showMini ? '100%' : undefined),
    marginBottom: ({ showMini }: StyleProps) =>
      showMini ? theme.spacing(3) : undefined,
    '&:first-of-type': {
      marginRight: theme.spacing(1),
    },
  },
}));

const pageSizes: PageSize[] = [5, 10, 15 as const];

type Props = {
  accountListParams: AccountListState;
  totalCount: number;
  onAccountSearch: (search: string) => void;
  onAccountSort: (value: AccountSort) => void;
  onAccountPageChange: (newPage: number, newPageSize: PageSize) => void;
  compact?: boolean;
};

export default function SortFilterPageHeader({
  accountListParams,
  totalCount = 0,
  onAccountSearch,
  onAccountSort,
  onAccountPageChange,
  compact = false,
}: Props) {
  const theme = useTheme();
  const smallDown = useMediaQuery(theme.breakpoints.down('sm'));
  const showMini = compact || smallDown;

  const classes = useStyles({ showMini });
  const { t } = useTranslation();

  return (
    <Grid container spacing={0}>
      <Grid item xs={12}>
        <div className={classes.formArea}>
          <div className={classes.formControl}>
            <AccountSearch
              onSearch={(search: string) => {
                onAccountSearch(search);
              }}
              value={accountListParams.query}
            />
          </div>
          <div className={classes.formControl}>
            <Dropdown
              label={t('SORT_BY')}
              value={accountListParams.sortBy}
              selectionList={[
                { value: 'DEFAULT', label: t('DEFAULT') },
                { value: 'ACCOUNTNUMBER', label: t('ACCOUNT_NUMBER') },
                { value: 'NICKNAME', label: t('NICKNAME') },
                { value: 'SERVICEADDRESS', label: t('SERVICE_ADDRESS') },
                { value: 'DUEDATE', label: t('DUE_DATE') },
              ]}
              onChange={e => onAccountSort(e.target.value as AccountSort)}
            />
          </div>
        </div>
      </Grid>

      {totalCount > pageSizes[0] && (
        <Grid item xs={12}>
          <Box className={classes.formControl} flexGrow={1}>
            <Pagination
              page={accountListParams.page}
              pageSize={accountListParams.pageSize}
              count={totalCount}
              pageSizes={pageSizes}
              onPageChange={(newPage: number, newPageSize: PageSize) => {
                onAccountPageChange(newPage, newPageSize);
              }}
            />
          </Box>
        </Grid>
      )}
    </Grid>
  );
}
